import { useState, useMemo, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import confetti from "canvas-confetti";
import {
  Check, X, Flame, Zap, Trophy, RotateCcw, ArrowRight, Star, PartyPopper,
  Timer, Hourglass, Play, Target, Share2, Shuffle, MessageCircle, Loader2, Lock,
} from "lucide-react";
import { saveScore, recordMastery, api, listMyStudyGroups, postStudyGroupReaction } from "@/lib/api";
import { FocusMode } from "@/components/FocusMode";
import { ShareGradeCard } from "@/components/ShareGradeCard";
import { TestimonialPromptModal } from "@/components/TestimonialPromptModal";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const POINTS = 100;
const STREAK_BONUS = 25;
const PER_Q = 20;

const gradeFor = (pct) => {
  if (pct >= 90) return { letter: "A", label: "Mastery Level", color: "hsl(var(--success))", emoji: "trophy" };
  if (pct >= 80) return { letter: "B", label: "Well Done", color: "hsl(var(--primary))", emoji: "star" };
  if (pct >= 70) return { letter: "C", label: "Solid Effort", color: "hsl(var(--accent))", emoji: "star" };
  if (pct >= 60) return { letter: "D", label: "Keep Studying", color: "hsl(var(--warning, 32 90% 45%))", emoji: "" };
  return { letter: "F", label: "Revisit the Lecture", color: "hsl(var(--destructive))", emoji: "" };
};

const CHEERS = ["Nice!", "Correct!", "Brilliant!", "You got it!", "Sharp!", "Exactly!"];
const MISSES = ["Not quite.", "So close!", "Almost!", "Good try."];

export const Quiz = ({ lecture, onScored }) => {
  const { user } = useAuth();
  const rawQuestions = lecture.quiz || [];
  const [idx, setIdx] = useState(0);
  const [selected, setSelected] = useState(null);
  const [locked, setLocked] = useState(false);
  const [answers, setAnswers] = useState([]);
  const [score, setScore] = useState(0);
  const [streak, setStreak] = useState(0);
  const [maxStreak, setMaxStreak] = useState(0);
  const [finished, setFinished] = useState(false);
  const [reviewing, setReviewing] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [myGroups, setMyGroups] = useState([]);
  const [postedToGroups, setPostedToGroups] = useState(false);
  // Load groups once on mount so the celebration banner can offer to post there.
  useEffect(() => {
    listMyStudyGroups()
      .then((r) => setMyGroups(r.groups || []))
      .catch(() => setMyGroups([]));
  }, []);
  const [started, setStarted] = useState(false);
  const [timed, setTimed] = useState(false);
  const [scramble, setScramble] = useState(() => localStorage.getItem("lectura_scramble") === "1");
  const [order, setOrder] = useState(() => rawQuestions.map((_, i) => i));
  const [explanations, setExplanations] = useState({}); // { [qIndex]: { loading, text, error, upsell } }
  const [timeLeft, setTimeLeft] = useState(PER_Q);
  const [showFocus, setShowFocus] = useState(false);

  const questions = useMemo(() => order.map((i) => rawQuestions[i]).filter(Boolean), [order, rawQuestions]);

  const q = questions[idx];
  const total = questions.length;
  const correctCount = answers.filter((a) => a.correct).length;
  const pct = total ? Math.round((correctCount / total) * 100) : 0;

  const pick = (i) => {
    if (locked) return;
    setSelected(i);
    setLocked(true);
    const correct = i === q.correct_index;
    const gained = correct ? POINTS + streak * STREAK_BONUS : 0;
    setScore((s) => s + gained);
    const newStreak = correct ? streak + 1 : 0;
    setStreak(newStreak);
    setMaxStreak((m) => Math.max(m, newStreak));
    setAnswers((a) => [...a, { qIndex: idx, chosen: i, correct }]);
    if (correct && newStreak >= 3) {
      confetti({ particleCount: 40, spread: 55, origin: { y: 0.7 }, scalar: 0.7 });
    }
  };

  // useRef guard: setIdx((i) => i + 1) is functional so spam-clicks stack. This
  // was silently skipping questions on rage-clicks. Lock until AnimatePresence
  // settles (~350ms) so the button responds to intent, not to every jitter.
  const advancingRef = useRef(false);
  const next = () => {
    if (advancingRef.current) return;
    advancingRef.current = true;
    setTimeout(() => { advancingRef.current = false; }, 380);
    if (idx + 1 >= total) {
      finish();
    } else {
      setIdx((i) => i + 1);
      setSelected(null);
      setLocked(false);
    }
  };

  const finish = () => {
    setFinished(true);
    const finalPct = Math.round((answers.filter((a) => a.correct).length / total) * 100);
    if (finalPct >= 80) {
      confetti({ particleCount: 160, spread: 100, origin: { y: 0.6 } });
      setTimeout(() => confetti({ particleCount: 80, angle: 60, spread: 70, origin: { x: 0 } }), 250);
      setTimeout(() => confetti({ particleCount: 80, angle: 120, spread: 70, origin: { x: 1 } }), 400);
      // Celebratory referral nudge — catches the dopamine spike right after a great score.
      const refCode = user?.referral_code;
      if (refCode) {
        const link = `${window.location.origin}/join/${refCode}`;
        setTimeout(() => {
          toast.success(`🎉 Nailed it — ${finalPct}%! Invite a friend to earn 30 days of Pro`, {
            duration: 12000,
            action: {
              label: "Copy invite link",
              onClick: () => {
                navigator.clipboard.writeText(link).then(
                  () => toast.success("Link copied — paste it in your group chat"),
                  () => toast.error("Couldn't copy — try again")
                );
              },
            },
          });
        }, 900);
      }
    }
    saveScore(lecture.id, finalPct)
      .then(() => recordMastery(lecture.id, Object.entries(byConcept).map(([concept, v]) => ({ concept, correct: v.correct, total: v.total }))))
      .then((updated) => onScored && onScored(updated))
      .catch(() => {});
  };

  const buildOrder = (shuffleFlag) => {
    const n = rawQuestions.length;
    const base = Array.from({ length: n }, (_, i) => i);
    if (!shuffleFlag) return base;
    // Fisher-Yates
    for (let i = n - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [base[i], base[j]] = [base[j], base[i]];
    }
    return base;
  };

  const toggleScramble = () => {
    const next = !scramble;
    setScramble(next);
    localStorage.setItem("lectura_scramble", next ? "1" : "0");
  };

  const explainDeeper = async (qq, i, a) => {
    if (explanations[i]?.loading) return;
    setExplanations((prev) => ({ ...prev, [i]: { loading: true } }));
    const correctOpt = qq.options?.[qq.correct_index];
    const chosenOpt = (a && a.chosen >= 0) ? qq.options?.[a.chosen] : null;
    const chosenLine = chosenOpt
      ? `I picked "${chosenOpt}" but the right answer is "${correctOpt}".`
      : `I couldn't answer in time. The right answer is "${correctOpt}".`;
    const question = `On the quiz you gave me for this lecture, one question asked: "${qq.question}"

${chosenLine}

Please explain — in 4-6 sentences — WHY "${correctOpt}" is right, WHY the other options are wrong, and give me a concrete example or memory hook so I never miss this again. Speak plainly, like you're teaching me one-on-one.`;
    try {
      const res = await api.post(`/lectures/${lecture.id}/ask`, { question, history: [] });
      setExplanations((prev) => ({ ...prev, [i]: { loading: false, text: res.data?.answer || res.data?.reply || "" } }));
    } catch (e) {
      const status = e?.response?.status;
      if (status === 402) {
        setExplanations((prev) => ({ ...prev, [i]: { loading: false, upsell: true } }));
      } else {
        setExplanations((prev) => ({ ...prev, [i]: { loading: false, error: e?.response?.data?.detail || "Couldn't reach the Professor — try again." } }));
      }
    }
  };

  const restart = () => {
    setIdx(0);
    setSelected(null);
    setLocked(false);
    setAnswers([]);
    setScore(0);
    setStreak(0);
    setMaxStreak(0);
    setFinished(false);
    setReviewing(false);
    setStarted(false);
    setTimeLeft(PER_Q);
    setShowFocus(false);
    setOrder(buildOrder(scramble));
  };

  const byConcept = useMemo(() => {
    const map = {};
    answers.forEach((a) => {
      const c = questions[a.qIndex]?.concept || "General";
      if (!map[c]) map[c] = { correct: 0, total: 0 };
      map[c].total += 1;
      if (a.correct) map[c].correct += 1;
    });
    return map;
  }, [answers, questions]);

  const weakConcepts = useMemo(
    () => Object.entries(byConcept).filter(([, v]) => v.correct < v.total).map(([c]) => c),
    [byConcept]
  );

  const handleTimeout = () => {
    setLocked(true);
    setStreak(0);
    setAnswers((a) => (a.some((x) => x.qIndex === idx) ? a : [...a, { qIndex: idx, chosen: -1, correct: false }]));
  };

  useEffect(() => {
    if (!started || !timed || locked || finished) return;
    setTimeLeft(PER_Q);
    const iv = setInterval(() => {
      setTimeLeft((t) => {
        if (t <= 1) {
          clearInterval(iv);
          setTimeout(handleTimeout, 0);
          return 0;
        }
        return t - 1;
      });
    }, 1000);
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idx, started, timed, locked, finished]);

  if (!total) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-16 text-center text-muted-foreground">
        No quiz available for this lecture.
      </div>
    );
  }

  // ---------------- Results ----------------
  if (finished) {
    if (showFocus) {
      return <FocusMode lecture={lecture} weakConcepts={weakConcepts} onBack={() => setShowFocus(false)} onUpdate={onScored} />;
    }
    const grade = gradeFor(pct);
    return (
      <div className="max-w-3xl mx-auto px-4 py-10">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ type: "spring", stiffness: 200, damping: 20 }}
          className="rounded-3xl border border-border bg-card shadow-lg overflow-hidden"
        >
          <div className="p-8 sm:p-12 text-center relative">
            <div className="flex justify-center mb-4 text-accent">
              {pct >= 80 ? <PartyPopper className="h-8 w-8" /> : <Trophy className="h-8 w-8" />}
            </div>
            <span className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">
              Your Grade
            </span>

            <div className="my-6 relative inline-grid place-items-center">
              <svg width="180" height="180" viewBox="0 0 180 180" className="-rotate-90">
                <circle cx="90" cy="90" r="78" fill="none" stroke="hsl(var(--secondary))" strokeWidth="14" />
                <motion.circle
                  cx="90" cy="90" r="78" fill="none" stroke={grade.color} strokeWidth="14"
                  strokeLinecap="round" strokeDasharray={2 * Math.PI * 78}
                  initial={{ strokeDashoffset: 2 * Math.PI * 78 }}
                  animate={{ strokeDashoffset: 2 * Math.PI * 78 * (1 - pct / 100) }}
                  transition={{ duration: 1.2, ease: "easeOut" }}
                />
              </svg>
              <div className="absolute inset-0 grid place-items-center">
                <motion.span
                  data-testid="quiz-percentage-score"
                  initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4 }}
                  className="font-serif text-5xl font-bold"
                >
                  {pct}%
                </motion.span>
                <span className="text-sm font-semibold" style={{ color: grade.color }}>
                  Grade {grade.letter}
                </span>
              </div>
            </div>

            <h2 className="font-serif text-3xl font-bold">{grade.label}</h2>
            <p className="mt-1 text-muted-foreground">
              {correctCount} of {total} correct · {score.toLocaleString()} pts · best streak {maxStreak} 🔥
            </p>

            {/* Concept breakdown */}
            <div className="mt-8 text-left space-y-3">
              <span className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">
                Breakdown by Concept
              </span>
              {Object.entries(byConcept).map(([c, v]) => {
                const p = Math.round((v.correct / v.total) * 100);
                return (
                  <div key={c}>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="font-medium">{c}</span>
                      <span className="text-muted-foreground font-mono">{v.correct}/{v.total}</span>
                    </div>
                    <div className="h-2 rounded-full bg-secondary overflow-hidden">
                      <motion.div
                        className="h-full rounded-full"
                        style={{ background: p >= 60 ? "hsl(var(--success))" : "hsl(var(--accent))" }}
                        initial={{ width: 0 }} animate={{ width: `${p}%` }} transition={{ duration: 0.8 }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>

            {lecture.concept_mastery && Object.keys(lecture.concept_mastery).length > 0 && (
              <div data-testid="mastery-tracker" className="mt-8 text-left">
                <span className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">
                  Mastery Tracker
                </span>
                <div className="mt-3 space-y-2">
                  {Object.entries(lecture.concept_mastery).map(([c, m]) => (
                    <div key={c} className="flex items-center justify-between gap-3 rounded-xl border border-border px-4 py-2.5">
                      <span className="text-sm font-medium">{c}</span>
                      <span className={`shrink-0 rounded-full px-2.5 py-1 text-xs font-bold ${
                        m.mastered ? "bg-[hsl(var(--success)/0.15)] text-[hsl(var(--success))]"
                        : m.best_pct >= 50 ? "bg-accent/15 text-accent"
                        : "bg-destructive/10 text-destructive"
                      }`}>
                        {m.mastered ? "Mastered" : m.best_pct >= 50 ? "Improving" : "Needs work"} · best {m.best_pct}% · {m.attempts}x
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {weakConcepts.length > 0 && (
              <button
                data-testid="quiz-focus-btn"
                onClick={() => setShowFocus(true)}
                className="mt-6 w-full flex items-center justify-center gap-2 rounded-2xl border-2 border-accent bg-accent/10 text-accent px-5 py-4 font-semibold hover:bg-accent/20 transition"
              >
                <Target className="h-5 w-5" />
                Focus Mode: master {weakConcepts.length} weak {weakConcepts.length === 1 ? "concept" : "concepts"}
              </button>
            )}

            {/* Celebration share nudge — appears only on great scores, catches the dopamine spike. */}
            {pct >= 80 && (
              <div
                data-testid="quiz-share-nudge"
                className="mt-8 rounded-2xl border-2 border-accent/40 bg-gradient-to-br from-accent/10 via-accent/5 to-background p-5 sm:p-6 text-left"
              >
                <div className="flex items-start gap-3">
                  <span className="grid place-items-center h-10 w-10 rounded-xl bg-accent text-accent-foreground shrink-0">
                    <PartyPopper className="h-5 w-5" />
                  </span>
                  <div className="flex-1">
                    <p className="font-serif text-lg sm:text-xl font-bold">You crushed it — post it while the win is fresh.</p>
                    <p className="mt-1 text-sm text-muted-foreground">
                      One 15-second TikTok or Reels post — with your A-grade share card — is how most first paying users find Lectura.
                      Plus, every friend who signs up gets you 30 days of Pro free.
                    </p>
                    <div className="mt-3 flex flex-wrap gap-2">
                      <button
                        data-testid="quiz-share-nudge-cta"
                        onClick={() => setShareOpen(true)}
                        className="inline-flex items-center gap-2 rounded-full bg-accent text-accent-foreground px-4 py-2 text-sm font-bold hover:opacity-90"
                      >
                        <Share2 className="h-4 w-4" /> Get my share card
                      </button>
                      <button
                        data-testid="quiz-feedback-nudge-cta"
                        onClick={() => setFeedbackOpen(true)}
                        className="inline-flex items-center gap-2 rounded-full border-2 border-border px-4 py-2 text-sm font-semibold hover:bg-secondary/60"
                      >
                        <Star className="h-4 w-4" /> Leave a 5-star reaction
                      </button>
                      {myGroups.length > 0 && !postedToGroups && (
                        <button
                          data-testid="quiz-post-to-groups"
                          onClick={async () => {
                            const grade = gradeFor(pct);
                            const posts = myGroups.map((g) =>
                              postStudyGroupReaction(g.code, {
                                lecture_id: lecture.id,
                                lecture_title: lecture.title,
                                grade_letter: grade.letter,
                                pct,
                                message: "",
                              }).catch(() => null)
                            );
                            await Promise.all(posts);
                            setPostedToGroups(true);
                            toast.success(`Posted your ${grade.letter} to ${myGroups.length} study group${myGroups.length === 1 ? "" : "s"}.`);
                          }}
                          className="inline-flex items-center gap-2 rounded-full border-2 border-border px-4 py-2 text-sm font-semibold hover:bg-secondary/60"
                        >
                          <Trophy className="h-4 w-4" /> Post grade to my study group{myGroups.length === 1 ? "" : "s"}
                        </button>
                      )}
                      {postedToGroups && (
                        <span data-testid="quiz-post-to-groups-done" className="inline-flex items-center gap-2 rounded-full bg-emerald-100 text-emerald-800 px-4 py-2 text-sm font-semibold">
                          <Check className="h-4 w-4" /> Posted to your group{myGroups.length === 1 ? "" : "s"}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
              <button
                data-testid="quiz-share-grade-btn"
                onClick={() => setShareOpen(true)}
                className="flex items-center justify-center gap-2 rounded-full bg-accent text-accent-foreground px-6 py-3 font-semibold hover:opacity-90 transition-opacity"
              >
                <Share2 className="h-4 w-4" /> Share your grade
              </button>
              <button
                data-testid="quiz-retry-btn"
                onClick={restart}
                className="flex items-center justify-center gap-2 rounded-full bg-primary text-primary-foreground px-6 py-3 font-semibold hover:opacity-90 transition-opacity"
              >
                <RotateCcw className="h-4 w-4" /> Try Again
              </button>
              <button
                data-testid="quiz-review-btn"
                onClick={() => setReviewing((r) => !r)}
                className="flex items-center justify-center gap-2 rounded-full border border-border px-6 py-3 font-semibold hover:border-primary/50 transition-colors"
              >
                {reviewing ? "Hide Review" : "Review Answers"}
              </button>
            </div>

            <ShareGradeCard open={shareOpen} onClose={() => setShareOpen(false)} pct={pct} lecture={lecture} />
            <TestimonialPromptModal
              open={feedbackOpen}
              onClose={() => setFeedbackOpen(false)}
              lecture={lecture}
              pct={pct}
              defaultDisplayName={user?.name ? user.name.split(" ")[0] + (user.name.split(" ")[1] ? ` ${user.name.split(" ")[1][0]}.` : "") : ""}
            />
          </div>
        </motion.div>

        {reviewing && (
          <div className="mt-6 space-y-4">
            {questions.map((qq, i) => {
              const a = answers.find((x) => x.qIndex === i);
              return (
                <div key={i} className="rounded-xl border border-border bg-card p-5">
                  <p className="font-semibold mb-3">{i + 1}. {qq.question}</p>
                  {qq.section && (
                    <p className="text-xs text-muted-foreground font-mono mb-2">From {qq.section}</p>
                  )}
                  <div className="space-y-2">
                    {qq.options.map((opt, oi) => {
                      const isCorrect = oi === qq.correct_index;
                      const isChosen = a && a.chosen === oi;
                      return (
                        <div
                          key={oi}
                          className={`flex items-center gap-2 rounded-lg px-3 py-2 text-sm ${
                            isCorrect ? "bg-[hsl(var(--success)/0.12)] text-foreground"
                            : isChosen ? "bg-destructive/10" : ""
                          }`}
                        >
                          {isCorrect ? <Check className="h-4 w-4 text-[hsl(var(--success))]" />
                            : isChosen ? <X className="h-4 w-4 text-destructive" />
                            : <span className="h-4 w-4" />}
                          <span>{opt}</span>
                        </div>
                      );
                    })}
                  </div>
                  {qq.explanation && (
                    <p className="mt-3 text-sm text-muted-foreground italic border-l-2 border-accent pl-3">
                      {qq.explanation}
                    </p>
                  )}
                  {(() => {
                    const ex = explanations[i];
                    const alreadyOpen = ex && (ex.loading || ex.text || ex.error || ex.upsell);
                    return (
                      <>
                        <button
                          data-testid={`explain-deeper-${i}`}
                          onClick={() => explainDeeper(qq, i, a)}
                          disabled={ex?.loading || !!ex?.text}
                          className="mt-3 inline-flex items-center gap-2 rounded-full border-2 border-border px-4 py-2 text-xs font-semibold hover:border-accent/60 hover:bg-accent/5 transition disabled:opacity-70"
                        >
                          {ex?.loading ? <><Loader2 className="h-3.5 w-3.5 animate-spin" /> The Professor is thinking…</>
                            : ex?.text ? <><Check className="h-3.5 w-3.5 text-accent" /> Explained by the Professor</>
                            : <><MessageCircle className="h-3.5 w-3.5" /> Explain this deeper</>}
                        </button>
                        {alreadyOpen && ex?.text && (
                          <div data-testid={`explain-deeper-body-${i}`} className="mt-3 rounded-xl border border-accent/40 bg-accent/5 p-4">
                            <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-accent mb-2 flex items-center gap-1.5">
                              <MessageCircle className="h-3 w-3" /> The Professor
                            </p>
                            <p className="text-sm leading-relaxed whitespace-pre-wrap">{ex.text}</p>
                          </div>
                        )}
                        {ex?.upsell && (
                          <div className="mt-3 rounded-xl border border-accent/50 bg-accent/10 p-4 flex items-center gap-3">
                            <Lock className="h-4 w-4 text-accent shrink-0" />
                            <div className="flex-1 text-sm">
                              <p className="font-semibold">The Professor is a Pro feature.</p>
                              <p className="text-muted-foreground text-xs mt-0.5">Upgrade to get unlimited per-question deep explanations.</p>
                            </div>
                          </div>
                        )}
                        {ex?.error && (
                          <p className="mt-2 text-xs text-destructive">{ex.error}</p>
                        )}
                      </>
                    );
                  })()}
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  // ---------------- Start Screen ----------------
  if (!started) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="rounded-3xl border border-border bg-card shadow-lg p-8 sm:p-12 text-center"
        >
          <div className="flex justify-center mb-4 text-accent"><Trophy className="h-8 w-8" /></div>
          <span className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">
            Interactive Quiz
          </span>
          <h2 className="mt-2 font-serif text-3xl sm:text-4xl font-bold">
            {total} Questions · Beat Your Best
          </h2>
          <p className="mt-3 text-muted-foreground max-w-md mx-auto">
            Earn points, build streaks for bonus points, and get a percentage grade at the end.
          </p>
          {lecture.best_score != null && (
            <p className="mt-2 text-sm font-semibold text-accent">Your best score: {lecture.best_score}%</p>
          )}
          <button
            onClick={() => setTimed((t) => !t)}
            data-testid="quiz-timed-toggle"
            className={`mt-6 inline-flex items-center gap-2 rounded-full border-2 px-5 py-3 font-semibold transition-all ${
              timed ? "border-accent bg-accent/10 text-accent" : "border-border text-muted-foreground hover:border-primary/50"
            }`}
          >
            <Timer className="h-4 w-4" />
            Timed Challenge: {timed ? `ON · ${PER_Q}s per question` : "OFF"}
          </button>
          <div className="mt-3">
            <button
              onClick={toggleScramble}
              data-testid="quiz-scramble-toggle"
              className={`inline-flex items-center gap-2 rounded-full border-2 px-5 py-3 font-semibold transition-all ${
                scramble ? "border-accent bg-accent/10 text-accent" : "border-border text-muted-foreground hover:border-primary/50"
              }`}
            >
              <Shuffle className="h-4 w-4" />
              Scramble Questions: {scramble ? "ON" : "OFF"}
            </button>
          </div>
          <div className="mt-8">
            <button
              data-testid="quiz-start-btn"
              onClick={() => { setOrder(buildOrder(scramble)); setStarted(true); setTimeLeft(PER_Q); }}
              className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-8 py-3.5 font-semibold hover:opacity-90 transition"
            >
              <Play className="h-4 w-4" /> Start Quiz
            </button>
          </div>
        </motion.div>
      </div>
    );
  }

  // ---------------- Active Quiz ----------------
  const isCorrect = selected === q.correct_index;
  return (
    <div className="max-w-3xl mx-auto px-4 py-8">
      {/* HUD */}
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3">
          <span className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">
            Question {idx + 1} / {total}
          </span>
        </div>
        <div className="flex items-center gap-3">
          <motion.div
            key={score}
            initial={{ scale: 1.3 }} animate={{ scale: 1 }}
            className="flex items-center gap-1.5 rounded-full bg-secondary px-3 py-1.5 text-sm font-semibold"
          >
            <Zap className="h-4 w-4 text-accent" /> {score.toLocaleString()}
          </motion.div>
          <div className={`flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm font-semibold transition-colors ${
            streak > 0 ? "bg-accent/15 text-accent" : "bg-secondary text-muted-foreground"
          }`}>
            <Flame className="h-4 w-4" /> {streak}
          </div>
          {timed && (
            <div
              data-testid="quiz-timer"
              className={`flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm font-semibold ${
                timeLeft <= 5 && !locked ? "bg-destructive/15 text-destructive animate-pulse" : "bg-secondary text-muted-foreground"
              }`}
            >
              <Timer className="h-4 w-4" /> {timeLeft}s
            </div>
          )}
        </div>
      </div>

      {/* Progress */}
      <div className="h-2 rounded-full bg-secondary overflow-hidden mb-4">
        <motion.div
          className="h-full bg-primary rounded-full"
          animate={{ width: `${((idx) / total) * 100}%` }}
          transition={{ duration: 0.4 }}
        />
      </div>

      {/* Standards-coverage bar — shows the compliance value at a glance */}
      {(() => {
        const codes = Array.from(new Set(questions.flatMap((qq) => qq.standards || []).filter(Boolean)));
        if (codes.length === 0) return null;
        return (
          <div className="mb-6 rounded-xl border border-accent/30 bg-accent/5 px-4 py-2.5 flex flex-wrap items-center gap-2" data-testid="standards-coverage">
            <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-accent">
              Standards covered · {codes.length}
            </span>
            <div className="flex flex-wrap gap-1.5">
              {codes.slice(0, 6).map((c, i) => (
                <span key={i} className="rounded-full bg-background border border-accent/30 px-2 py-0.5 text-[10px] font-mono font-semibold text-foreground/80">
                  {c}
                </span>
              ))}
              {codes.length > 6 && (
                <span className="rounded-full bg-background border border-accent/30 px-2 py-0.5 text-[10px] font-mono font-semibold text-muted-foreground">
                  +{codes.length - 6} more
                </span>
              )}
            </div>
          </div>
        );
      })()}

      <AnimatePresence mode="wait">
        <motion.div
          key={idx}
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -24 }}
          transition={{ duration: 0.3 }}
        >
          {(q.section || (q.standards && q.standards.length > 0)) && (
            <div className="flex flex-wrap items-center gap-2 mb-3">
              {q.section && (
                <span data-testid="quiz-question-section" className="rounded-full bg-secondary px-3 py-1 text-xs font-semibold text-muted-foreground font-mono">
                  {q.section}
                </span>
              )}
              {(q.standards || []).map((s, si) => (
                <span
                  key={si}
                  data-testid={`quiz-standard-${si}`}
                  title="Academic standard this question maps to"
                  className="rounded-full bg-accent/10 text-accent px-2.5 py-1 text-[10px] font-bold font-mono tracking-wide uppercase"
                >
                  {s}
                </span>
              ))}
            </div>
          )}
          <h2 className="font-serif text-2xl sm:text-3xl font-semibold leading-snug mb-6">
            {q.question}
          </h2>

          <div className="space-y-3">
            {q.options.map((opt, i) => {
              const chosen = selected === i;
              const showCorrect = locked && i === q.correct_index;
              const showWrong = locked && chosen && i !== q.correct_index;
              return (
                <motion.button
                  key={i}
                  data-testid={`quiz-option-${i}`}
                  onClick={() => pick(i)}
                  disabled={locked}
                  whileTap={{ scale: locked ? 1 : 0.98 }}
                  className={`w-full text-left flex items-center gap-4 rounded-2xl border-2 px-5 py-4 transition-all ${
                    showCorrect ? "border-[hsl(var(--success))] bg-[hsl(var(--success)/0.1)]"
                    : showWrong ? "border-destructive bg-destructive/10"
                    : locked ? "border-border opacity-60"
                    : "border-border hover:border-primary hover:-translate-y-0.5"
                  }`}
                >
                  <span className={`grid place-items-center h-8 w-8 rounded-full font-semibold text-sm shrink-0 ${
                    showCorrect ? "bg-[hsl(var(--success))] text-white"
                    : showWrong ? "bg-destructive text-white"
                    : "bg-secondary"
                  }`}>
                    {showCorrect ? <Check className="h-4 w-4" /> : showWrong ? <X className="h-4 w-4" /> : String.fromCharCode(65 + i)}
                  </span>
                  <span className="font-medium">{opt}</span>
                </motion.button>
              );
            })}
          </div>

          <AnimatePresence>
            {locked && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                className="mt-5 overflow-hidden"
              >
                <div className={`rounded-2xl p-5 ${isCorrect ? "bg-[hsl(var(--success)/0.1)]" : "bg-secondary"}`}>
                  <div className="flex items-center gap-2 font-semibold mb-1">
                    {isCorrect ? (
                      <>
                        <Star className="h-4 w-4 text-[hsl(var(--success))]" />
                        <span className="text-[hsl(var(--success))]">
                          {CHEERS[idx % CHEERS.length]} {streak > 1 && `+${streak * STREAK_BONUS} streak bonus`}
                        </span>
                      </>
                    ) : (
                      <>
                        {selected === null ? (
                          <Hourglass className="h-4 w-4 text-destructive" />
                        ) : (
                          <X className="h-4 w-4 text-destructive" />
                        )}
                        <span className="text-destructive">
                          {selected === null ? "Time's up!" : MISSES[idx % MISSES.length]}
                        </span>
                      </>
                    )}
                  </div>
                  {q.explanation && <p className="text-sm text-foreground/80 leading-relaxed">{q.explanation}</p>}
                </div>
                <button
                  data-testid="quiz-next-btn"
                  onClick={next}
                  disabled={!locked}
                  className="mt-4 w-full flex items-center justify-center gap-2 rounded-full bg-primary text-primary-foreground py-3.5 font-semibold hover:opacity-90 transition-opacity disabled:opacity-60 disabled:cursor-not-allowed active:scale-[0.98]"
                >
                  {idx + 1 >= total ? "See My Grade" : "Next Question"}
                  <ArrowRight className="h-4 w-4" />
                </button>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </AnimatePresence>
    </div>
  );
};
